import { Box, Divider, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ProjectIcon } from "../../common/components/ProjectIcon";
import { useAuth } from "../../common/contexts/AuthContext";
import { truncateString } from "../../common/utils/format_utils";
import { getMyProjects, switchProject } from "../../projects/api/project";

export const ProjectSelector = () => {
  const [projectMenuAnchor, setProjectMenuAnchor] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleProjectMenuOpen = (event) => {
    setProjectMenuAnchor(event.currentTarget);
  };
  const handleSwitchProject = async (project_id) => {
    try {
      await switchProject(project_id);
      window.location.href = "/";
    } catch (error) {
      console.error("Error switching project:", error);
    }
  };

  const handleProjectMenuClose = () => {
    setProjectMenuAnchor(null);
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const fetchedMyProjects = await getMyProjects();
        setMyProjects(fetchedMyProjects);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    fetchProjects();
  }, []);

  // Always render, but handle empty states
  const currentProject = user?.project || user?.currently_selected_project;

  return (
    <Box sx={{ py: 2, px: 2, width: "100%", boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Paper
          elevation={0}
          data-testid="project-selector"
          sx={{
            p: 1.5,
            backgroundColor: "transparent",
            borderRadius: 2,
            cursor: currentProject ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            "&:hover": {
              backgroundColor: currentProject ? "action.hover" : "transparent",
            },
          }}
          onClick={currentProject ? handleProjectMenuOpen : undefined}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              gap: 1.5,
            }}
          >
            <ProjectIcon
              name={currentProject?.name || "No Project"}
              icon={currentProject?.icon}
              size={44}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "16px",
                  lineHeight: 1.3,
                  color: currentProject ? "text.primary" : "text.secondary",
                }}
              >
                {currentProject?.name || ""}
              </Typography>
              {currentProject?.description ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                    lineHeight: 1.3,
                  }}
                >
                  {truncateString(currentProject.description, 20)}
                </Typography>
              ) : (
                ""
              )}
            </Box>
          </Box>
          {currentProject && (
            <ChevronDown size={18} style={{ flexShrink: 0 }} />
          )}
        </Paper>
      </Box>

      <Menu
        anchorEl={projectMenuAnchor}
        open={Boolean(projectMenuAnchor)}
        onClose={handleProjectMenuClose}
        PaperProps={{
          sx: {
            width: 320,
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
        MenuListProps={{
          sx: { py: 1 },
        }}
      >
        {myProjects.length > 0 ? (
          myProjects.map((myProject, index) => (
            <MenuItem
              key={myProject.id}
              onClick={() => handleSwitchProject(myProject.id)}
              selected={myProject.id === currentProject?.id}
              sx={{
                py: 1.5,
                px: 2,
                mx: 1,
                mb: index < myProjects.length - 1 ? 0.5 : 0,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                  "&:hover": {
                    backgroundColor: "action.selected",
                  },
                },
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ProjectIcon
                name={myProject?.name}
                icon={myProject?.icon}
                size={36}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {myProject?.name}
                </Typography>
                {myProject?.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "11px",
                    }}
                  >
                    {truncateString(myProject?.description, 30)}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))
        ) : (
          <Box sx={{ py: 2, px: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No projects available
            </Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={() => {
            navigate("/my-projects");
            handleProjectMenuClose();
          }}
          sx={{
            py: 1.5,
            px: 2,
            mx: 1,
            borderRadius: 1.5,
            color: "primary.main",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          View all projects
        </MenuItem>
      </Menu>
    </Box>
  );
};
